import { ConnectionDetails, QueryInputType, convertConnectionToDetails, mergeConnectionDetails as merge, queryInput } from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  IncidentIssueWithCache,
  IncidentIssueWithCacheSearch,
  SearchIncidentIssueWithCacheResponseIncidentIssueWithCacheConnectionArgs,
  SortDirectionEnum,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';
import {
  createIncidentIssueDocumentDownloadUrl,
  recreateIncidentIssueDocumentDownloadUrl,
} from '@/features/IncidentHistory/api/services/createIncidentIssueDocumentDownloadURL';
import { refetchIncidentIssueDocument, searchIncidentIssueDocument } from './searchIncidentIssueDocument';

import { IncidentIssueWithDocuments } from '@/features/IncidentHistory/components/IncidentHistoryForm/types';
import { SearchIncidentIssue } from '@/features/IncidentHistory/api/queries/SearchIncidentIssue';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  airportCode?: string[];
  tripServicerIataAirlineCodes?: string[];
} & { [key in keyof IncidentIssueWithCacheSearch]?: any })[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<IncidentIssueWithCache>>;
}
interface ServiceMethodWithDocuments {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<IncidentIssueWithDocuments>>;
}

// SearchIncidentIssue Service
export const { searchIncidentIssue, refetchIncidentIssue, useIncidentIssue } = ((): {
  searchIncidentIssue: ServiceMethod;
  refetchIncidentIssue: ServiceMethod;
  useIncidentIssue: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchIncidentIssue).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<IncidentIssueWithCache>> => {
        const query: IncidentIssueWithCacheSearch[] = payload?.map((item) => ({
          tripId: queryInput.orNull(item?.id),
          createdAt:
            item?.startDatetime && item?.endDatetime
              ? queryInput.date([item?.startDatetime, item.endDatetime], false, SortDirectionEnum.Desc)
              : item?.id // If we are searching by trip id this is a history table, and we can get all the dispatch issues and sort by datetime
                ? queryInput([], QueryInputType.ISNOTNULL, SortDirectionEnum.Desc)
                : null,
          station: queryInput.orNull(item?.airportCode),
          tripServicerIataAirlineCode: queryInput.orNull(item?.tripServicerIataAirlineCodes),
        }));
        const args: SearchIncidentIssueWithCacheResponseIncidentIssueWithCacheConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query,
          },
        };
        const opts = { merge: options?.merge ? 'searchIncidentIssue.incidentIssueTableConnection' : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty('searchIncidentIssue.incidentIssueTableConnection', res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchIncidentIssue: fetch,
    refetchIncidentIssue: refetch,
    useIncidentIssue: useService,
  };
})();

export const { searchIncidentIssueWithDocuments, refetchIncidentIssueWithDocuments } = ((): {
  searchIncidentIssueWithDocuments: ServiceMethodWithDocuments;
  refetchIncidentIssueWithDocuments: ServiceMethodWithDocuments;
} => {
  const fetch = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<IncidentIssueWithDocuments>> => {
    const IncidentIssues = await searchIncidentIssue(payload, options);
    const res: ConnectionDetails<IncidentIssueWithDocuments> = {
      ...IncidentIssues,
      rows: IncidentIssues.rows.map((row) => ({
        ...row,
        documents: [],
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const documents = await searchIncidentIssueDocument([{ issueId: queryInput(allIds) }]);
      try {
        const downloadURLS = await createIncidentIssueDocumentDownloadUrl({ issueId: allIds });
        documents.rows.forEach((document) => {
          const issueIndex = res.rows.findIndex((row) => row.id === document.issueId);
          const downloadUrlIndex = downloadURLS.urls.findIndex((url) => url.documentId === document.id);
          if (issueIndex !== -1) {
            res.rows[issueIndex].documents.push({ ...document, fileLocation: downloadURLS.urls[downloadUrlIndex]?.url });
          }
        });
      } catch (err) {
        console.error('Error creating download URLs:', err);
      }
    }
    return { ...res };
  };

  const refetch = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<IncidentIssueWithDocuments>> => {
    const IncidentIssues = await refetchIncidentIssue(payload, options);
    const res: ConnectionDetails<IncidentIssueWithDocuments> = {
      ...IncidentIssues,
      rows: IncidentIssues.rows.map((row) => ({
        ...row,
        documents: [],
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const documents = await refetchIncidentIssueDocument([{ issueId: queryInput(allIds) }]);
      try {
        const downloadURLS = await recreateIncidentIssueDocumentDownloadUrl({ issueId: allIds });
        documents.rows.forEach((document) => {
          const issueIndex = res.rows.findIndex((row) => row.id === document.issueId);
          const downloadUrlIndex = downloadURLS.urls.findIndex((url) => url.documentId === document.id);
          if (issueIndex !== -1) {
            res.rows[issueIndex].documents.push({ ...document, fileLocation: downloadURLS.urls[downloadUrlIndex]?.url });
          }
        });
      } catch (err) {
        console.error('Error creating download URLs:', err);
      }
    }
    return { ...res };
  };
  return {
    searchIncidentIssueWithDocuments: fetch,
    refetchIncidentIssueWithDocuments: refetch,
  };
})();
